import { Code, X } from '@phosphor-icons/react';
import { useCallback, useEffect, useState } from 'react';
import {
  AutoCompleteMultiSelectBox,
  Option as MultiSelectOption,
} from 'src/components/AutoCompleteMultiSelectBox';
import { AutoCompleteSelectBox } from 'src/components/AutoCompleteSelectBox';
import { AutoCompleteSelectBoxControlled } from 'src/components/AutoCompleteSelectBoxControlled';
import { Button } from 'src/components/Button';
import { EquivalentCode } from 'src/components/EquivalentCode';
import { Label } from 'src/components/Label';
import { Modal } from 'src/components/Modal';
import { Option, SelectBox } from 'src/components/SelectBox';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Agent } from 'src/interfaces/agent.interface';
import { AvailableNumber } from 'src/interfaces/availableNumber.interface';
import { FormNumber, NumberTags } from 'src/interfaces/number.interface';
import { QueryObject } from 'src/interfaces/queryObject.interface';
import { providerOptions } from 'src/stubs/numbers.stub';
import {
  getAccountConnectionType,
  getTelephonyProviderType,
} from 'src/utils/telephony';
import {
  buildTagOptionsWithQuery,
  mergeTagOptions,
} from 'src/components/tag/tag.helpers';
import { AccountConnectionOption } from './numbersTypes';

interface BuyNumberProps {
  onClose?: () => void;
  refreshData?: () => void;
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  agents?: Option[];
  accountConnections?: AccountConnectionOption[];
}

export const BuyNumber = ({
  onClose = () => {},
  refreshData = () => {},
  modalOpen,
  setModalOpen,
  agents = [],
  accountConnections = [],
}: BuyNumberProps) => {
  const [equivalentCodeOpen, setEquivalentCodeOpen] = useState(false);
  const { get, post } = useAuthedRequest();
  const notification = useNotification();

  const [loading, setLoading] = useState(false);
  const [formNumber, setFormNumber] = useState<FormNumber>({
    telephony_provider: providerOptions[0].value,
    phone_number: '',
    telephony_account_connection: '',
    inbound_agent: '',
    tags: [],
  });
  const [numbersOptions, setNumbersOptions] = useState<Option[]>([]);
  const [tagOptions, setTagOptions] = useState<MultiSelectOption[]>([]);
  const [selectedTagOptions, setSelectedTagOptions] = useState<
    MultiSelectOption[]
  >([]);

  const closeModal = () => {
    setModalOpen(false);
    setEquivalentCodeOpen(false);
    setSelectedTagOptions([]);
    setTagOptions([]);
    setFormNumber((prevFormNumber) => ({
      ...prevFormNumber,
      tags: [],
    }));
    onClose();
  };

  const confirmBuyNumber = async () => {
    setLoading(true);

    const { inbound_agent, ...restFormNumber } = formNumber;

    if (
      !restFormNumber.telephony_account_connection ||
      restFormNumber.telephony_account_connection.length === 0
    ) {
      delete restFormNumber.telephony_account_connection;
    }

    try {
      const tagsPayload =
        formNumber.tags && formNumber.tags.length ? formNumber.tags : undefined;
      await post('/numbers/buy', {
        ...restFormNumber,
        ...(inbound_agent.length && { inbound_agent: inbound_agent }),
        ...(tagsPayload ? { tags: tagsPayload } : {}),
      });

      notification.success(`Number bought`);
      setModalOpen(false);
      refreshData();
      searchNumbers('');
      setFormNumber((prevFormNumber) => ({
        ...prevFormNumber,
        phone_number: '',
        tags: [],
      }));
      setSelectedTagOptions([]);
    } catch (error) {
      notification.error(
        `Number ${formNumber.phone_number} could not be purchased.`,
      );
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const isValidForm = (form: FormNumber) => {
    return !form.telephony_provider || !form.phone_number;
  };

  const disabledSubmit = () => {
    if (loading) return true;

    return isValidForm(formNumber);
  };

  const searchAgents = async (name: string) => {
    const queryObject: QueryObject = {
      ...(name.length !== 0 && { filters: JSON.stringify({ name }) }),
    };
    const query = new URLSearchParams(queryObject as Record<string, string>);

    const data = await get(`/agents?${query.toString()}`);

    return data.items.map((agent: Agent) => ({
      value: agent.id,
      label: agent.name,
    }));
  };

  const fetchNumberTagOptions = useCallback(
    async (query: string) => {
      const queryObject: QueryObject = {
        page: '1',
        size: '10',
      };
      const trimmedQuery = query.trim();
      if (trimmedQuery) {
        queryObject.filters = JSON.stringify({ startsWith: trimmedQuery });
      }
      const params = new URLSearchParams(queryObject as Record<string, string>);
      const data: NumberTags = await get(`/numbers/tags?${params.toString()}`);
      const tags = data.items || [];
      return buildTagOptionsWithQuery(tags, query);
    },
    [get],
  );

  const searchNumbers = useCallback(
    async (number: string) => {
      const currentProvider = formNumber.telephony_provider;
      const currentConnection = formNumber.telephony_account_connection;

      const queryObject: QueryObject & {
        telephony_provider: string;
        telephony_account_connection: string | undefined;
      } = {
        telephony_provider: currentProvider,
        telephony_account_connection: currentConnection,
        ...(number.length !== 0 && {
          filters: JSON.stringify({ contains: number }),
        }),
      };
      if (!queryObject.telephony_account_connection) {
        delete queryObject.telephony_account_connection;
      }

      const query = new URLSearchParams(
        queryObject as unknown as Record<string, string>,
      );
      const data = await get(`/numbers/available?${query.toString()}`);

      setNumbersOptions(
        data?.items.map((item: AvailableNumber) => ({
          value: item.number,
          label: item.number,
        })),
      );
    },
    [
      formNumber.telephony_provider,
      formNumber.telephony_account_connection,
      get,
    ],
  );

  const handleTagSearch = useCallback(
    async (query: string) => {
      const options = await fetchNumberTagOptions(query);
      const merged = mergeTagOptions(
        options,
        selectedTagOptions.map((option) => option.value),
      );
      setTagOptions(merged);
    },
    [fetchNumberTagOptions, selectedTagOptions],
  );

  const handleTagSelectionChange = (options: MultiSelectOption[]) => {
    setSelectedTagOptions(options);
    setFormNumber((prevFormNumber) => ({
      ...prevFormNumber,
      tags: options.map((option) => option.value),
    }));
  };

  useEffect(() => {
    // Trigger fetching numbers when the account connection changes
    searchNumbers('');
  }, [
    formNumber.telephony_provider,
    formNumber.telephony_account_connection,
    searchNumbers,
  ]);

  useEffect(() => {
    if (modalOpen) {
      handleTagSearch('');
    }
  }, [modalOpen, handleTagSearch]);

  return (
    <Modal
      title={`New Number`}
      isOpen={modalOpen}
      onClose={() => closeModal()}
      actionButton={
        <Button onClick={() => confirmBuyNumber()} disabled={disabledSubmit()}>
          Add Number
        </Button>
      }
    >
      <div className="flex justify-end mb-1">
        <Button
          onClick={() =>
            equivalentCodeOpen
              ? setEquivalentCodeOpen(false)
              : setEquivalentCodeOpen(true)
          }
          className="flex items-center justify-center"
        >
          <Code className="mr-2" size={20} />
          Show equivalent code
        </Button>
      </div>

      <div className="flex justify-end italic text-sm mt-4">
        * Required fields
      </div>

      <div className="space-y-6">
        {/* First row: Provider, Inbound Agent */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="telephony_provider" required>
              Provider
            </Label>

            <SelectBox
              options={providerOptions}
              variant="outlined"
              color="primary"
              size="medium"
              onChange={(value) => {
                const connectionType = getAccountConnectionType(value);
                const connection = accountConnections.find(
                  (item) => item.connectionType === connectionType,
                );
                setFormNumber((prevFormNumber) => ({
                  ...prevFormNumber,
                  telephony_provider: value,
                  telephony_account_connection: connection?.value || '',
                  phone_number: '',
                }));
              }}
              className="min-w-[140px]"
              value={
                formNumber.telephony_provider
                  ? providerOptions.find(
                      (item) => item.value === formNumber.telephony_provider,
                    ) || null
                  : null
              }
              disabled={loading}
            />
          </div>

          <div>
            <Label htmlFor="inbound-agent">Inbound Agent</Label>

            <AutoCompleteSelectBox
              defaultValue={
                formNumber.inbound_agent
                  ? agents.find(
                      (item) => item.value === formNumber.inbound_agent,
                    ) || undefined
                  : undefined
              }
              fetchOptions={searchAgents}
              onChange={(value) =>
                setFormNumber((prevFormNumber) => ({
                  ...prevFormNumber,
                  inbound_agent: value,
                }))
              }
              variant="outlined"
              color="primary"
              size="medium"
              disabled={loading}
            />
          </div>
        </div>

        {/* Second row: Account Connection, Tags */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="account-connection">Account Connection</Label>

            <SelectBox
              options={accountConnections}
              variant="outlined"
              color="primary"
              size="medium"
              onChange={(value) => {
                const option = accountConnections.find(
                  (item) => item.value === value,
                )!;
                const telephonyProvider = getTelephonyProviderType(
                  option.connectionType,
                );
                setFormNumber((prevFormNumber) => ({
                  ...prevFormNumber,
                  telephony_account_connection: option.value,
                  telephony_provider: telephonyProvider,
                  phone_number: '',
                }));
              }}
              className="min-w-[140px]"
              value={
                formNumber.telephony_account_connection
                  ? accountConnections.find(
                      (item) =>
                        item.value === formNumber.telephony_account_connection,
                    ) || null
                  : null
              }
              disabled={loading}
            />
          </div>

          <div>
            <Label>Tags</Label>

            <AutoCompleteMultiSelectBox
              selectedOptions={selectedTagOptions}
              fetchOptions={handleTagSearch}
              onSelectionChange={handleTagSelectionChange}
              options={tagOptions}
              placeholder="Add tags"
              noOptionsText="No tags yet"
            />
          </div>
        </div>

        {/* Third row: Number (half width) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="available-numbers" required>
              Number
            </Label>

            <AutoCompleteSelectBoxControlled
              value={
                formNumber.phone_number
                  ? {
                      value: String(formNumber.phone_number),
                      label: String(formNumber.phone_number),
                    }
                  : null
              }
              fetchOptions={searchNumbers}
              onChange={(value) => {
                setFormNumber((prevFormNumber) => ({
                  ...prevFormNumber,
                  phone_number: value || '',
                }));
              }}
              className="min-w-[140px]"
              variant="outlined"
              color="primary"
              size="medium"
              disabled={loading}
              options={numbersOptions}
            />
          </div>
        </div>
      </div>

      {equivalentCodeOpen && (
        <div className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <h3>Equivalent Code</h3>

            <X
              className="cursor-pointer inline-block w-5 h-5"
              onClick={() => setEquivalentCodeOpen(false)}
            />
          </div>

          <EquivalentCode
            payload={{
              ...formNumber,
            }}
            endpoint={`numbers/buy`}
            method="POST"
          />
        </div>
      )}
    </Modal>
  );
};
